import type { CreateMeetingRequest, CreateMeetingResponse, MeetingDto, MeetingResultResponse } from "@meeting-notes/shared";
import { useAuth } from "react-oidc-context";
import { useMemo } from "react";
import { useConfig } from "./use-config";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

export function createApi(base: string, getToken: () => string | undefined) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = getToken();
    const res = await fetch(`${base}${path}`, {
      method,
      headers: { ...(body ? { "content-type": "application/json" } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 204) return undefined as T;
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    if (!res.ok) throw new ApiError(res.status, data.error ?? "error", data.message ?? `HTTP ${res.status}`);
    return data as T;
  }
  return {
    me: () => call<{ sub: string; email: string | null; name: string | null }>("GET", "/me"),
    createMeeting: (input: CreateMeetingRequest) => call<CreateMeetingResponse>("POST", "/meetings", input),
    listMeetings: (cursor?: string) => call<{ items: MeetingDto[]; cursor: string | null }>("GET", `/meetings${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`),
    getMeeting: (id: string) => call<{ meeting: MeetingDto }>("GET", `/meetings/${encodeURIComponent(id)}`),
    getResult: (id: string) => call<MeetingResultResponse>("GET", `/meetings/${encodeURIComponent(id)}/result`),
    deleteMeeting: (id: string) => call<void>("DELETE", `/meetings/${encodeURIComponent(id)}`),
    subscribePush: (sub: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) => call<void>("PUT", "/push/subscription", sub),
    unsubscribePush: (endpoint: string) => call<void>("DELETE", "/push/subscription", { endpoint }),
    vapidPublicKey: () => call<{ publicKey: string }>("GET", "/push/vapid-public-key"),
  };
}

export type Api = ReturnType<typeof createApi>;

export function useApi(): Api {
  const auth = useAuth();
  const cfg = useConfig();
  const token = auth.user?.id_token;
  return useMemo(() => createApi(cfg.apiBase, () => token), [cfg.apiBase, token]);
}
